import { lazy } from 'react';
import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import { PAGE_TITLES } from '@/constants/titles';
import BaseLayout from '@/layouts/base.layout';
import MainLayout from '@/layouts/main.layout';
import AuthLayout from '@/layouts/auth.layout';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { lazyRetry } from '@/utils/lazy-retry';

// Lazy load các components
//module
const StoresPage = lazyRetry(() => import(/* webpackChunkName: "stores"*/ '@/modules/stores'));
const StoreDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "store-detail"*/ '@/modules/stores/detail'),
);

const OrderPage = lazy(() => import(/* webpackChunkName: "order"*/ '@/modules/order'));
const OrderDetailPage = lazy(
  () => import(/* webpackChunkName: "order-detail"*/ '@/modules/order/OrderDetail.page'),
);

const RolesPage = lazy(() => import(/* webpackChunkName: "roles"*/ '@/modules/roles'))

const DepartmentPage = lazy(
  () => import(/* webpackChunkName: "department"*/ '@/modules/department'),
);
const DepartmentDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "department-detail"*/ '@/modules/department/detail'),
);

//Auth
const LoginPage = lazy(() => import(/* webpackChunkName: "login"*/ '@/pages/Auth/Login'));

//not-found
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <WrapperRouteComponent title="">
            <MainLayout />
          </WrapperRouteComponent>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/stores" replace />,
      },
      {
        path: '/stores',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STORES}>
            <StoresPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/stores/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STORES}>
            <StoreDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/orders',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/orders/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/roles',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ROLES}>
            <RolesPage />
          </WrapperRouteComponent>
        )
      },
      {
        path: '/departments',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DEPARTMENTS}>
            <DepartmentPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/departments/:departmentId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DEPARTMENTS}>
            <DepartmentDetailPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <LoginPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthWrapper>
        <WrapperRouteComponent title="">
          <Middleware mode="private">
            <BaseLayout />
          </Middleware>
        </WrapperRouteComponent>
      </AuthWrapper>
    ),
    children: [],
  },
  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
